import React, { useCallback, useContext, useEffect, useState } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Pencil, Trash2, Plus, Loader2, Globe2, Users } from "lucide-react";
import { format, parseISO } from "date-fns";

import { AuthContext } from "../../context/Auth/AuthContext";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import AnnouncementsMarkdown from "../../components/AnnouncementsMarkdown";
import { cn } from "../../lib/utils";

const PRIORIDADES = {
	1: { rotulo: "Normal", variante: "outline-solid" },
	2: { rotulo: "Importante", variante: "secondary" },
	3: { rotulo: "Urgente", variante: "destructive" },
};

const AnnouncementSchema = Yup.object().shape({
	title: Yup.string().min(2, "Título muito curto").required("Informe o título"),
	text: Yup.string().min(2, "Texto muito curto").required("Escreva o aviso"),
});

const Announcements = () => {
	const { user } = useContext(AuthContext);
	const ehSuperAdmin =
		user?.profile === "admin" && Number(user?.companyId) === 1;

	const [announcements, setAnnouncements] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const carregar = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/announcements");
			setAnnouncements(data);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const fecharModal = () => {
		setModalOpen(false);
		setSelecionado(null);
	};

	const salvar = async (values) => {
		try {
			if (selecionado?.id) {
				await api.put(`/announcements/${selecionado.id}`, values);
			} else {
				await api.post("/announcements", values);
			}
			toast.success("Aviso salvo.");
			fecharModal();
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/announcements/${id}`);
			toast.success("Aviso excluído.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	const valoresIniciais = {
		title: selecionado?.title || "",
		text: selecionado?.text || "",
		priority: selecionado?.priority || 1,
		isActive: selecionado?.isActive ?? true,
		isGlobal: selecionado?.id
			? Boolean(selecionado?.isGlobal)
			: ehSuperAdmin,
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionado && `Excluir "${selecionado.title}"?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={() => excluir(selecionado.id)}
			>
				O aviso deixa de aparecer para a equipe.
			</ConfirmationModal>

			<Dialog
				open={modalOpen}
				onOpenChange={(aberto) => !aberto && fecharModal()}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{selecionado?.id ? "Editar aviso" : "Novo aviso"}
						</DialogTitle>
					</DialogHeader>
					<Formik
						initialValues={valoresIniciais}
						enableReinitialize
						validationSchema={AnnouncementSchema}
						onSubmit={(values, actions) => {
							salvar(values);
							actions.setSubmitting(false);
						}}
					>
						{({ touched, errors, isSubmitting, values, setFieldValue }) => (
							<Form className="space-y-4">
								<div className="space-y-1.5">
									<Label htmlFor="title">Título</Label>
									<Field as={Input} id="title" name="title" autoFocus />
									{touched.title && errors.title && (
										<p className="text-xs text-destructive">{errors.title}</p>
									)}
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="text">Aviso (suporta Markdown: **negrito**, _itálico_, emoji, listas, links)</Label>
									<Field as={Textarea} id="text" name="text" rows={5} />
									{touched.text && errors.text && (
										<p className="text-xs text-destructive">{errors.text}</p>
									)}
									<div className="mt-2">
										<div className="mb-1 flex items-center justify-between">
											<p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
												Pré-visualização
											</p>
											<div className="flex gap-2 text-[10px] text-muted-foreground">
												<span>**negrito**</span>
												<span>·</span>
												<span>_itálico_</span>
												<span>·</span>
												<span>- item</span>
												<span>·</span>
												<span>[texto](https://...)</span>
											</div>
										</div>
										<div className="rounded-lg border bg-muted/30 p-3 min-h-[80px]">
											{values.text?.trim() ? (
												<AnnouncementsMarkdown>{values.text}</AnnouncementsMarkdown>
											) : (
												<p className="text-xs italic text-muted-foreground">
													O texto aqui aparece formatado enquanto você digita.
												</p>
											)}
										</div>
									</div>
								</div>

								<div className="space-y-1.5">
									<Label>Prioridade</Label>
									<Select
										value={String(values.priority)}
										onValueChange={(valor) =>
											setFieldValue("priority", Number(valor))
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="1">Normal</SelectItem>
											<SelectItem value="2">Importante</SelectItem>
											<SelectItem value="3">Urgente</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="flex items-center justify-between rounded-lg border p-3">
									<div>
										<Label htmlFor="isActive" className="text-sm font-medium">
											Aviso ativo
										</Label>
										<p className="mt-0.5 text-xs text-muted-foreground">
											Desative para arquivar sem excluir.
										</p>
									</div>
									<Switch
										id="isActive"
										checked={values.isActive}
										onCheckedChange={(marcado) =>
											setFieldValue("isActive", marcado)
										}
									/>
								</div>

								{ehSuperAdmin && (
									<div className="flex items-center justify-between rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
										<div className="flex items-start gap-2">
											<Globe2 className="mt-0.5 h-4 w-4 text-primary shrink-0" />
											<div>
												<Label
													htmlFor="isGlobal"
													className="text-sm font-semibold text-primary"
												>
													Enviar para TODAS as empresas clientes (SAAS dono do sistema)
												</Label>
												<p className="mt-0.5 text-xs text-muted-foreground">
													Comunicado global. Chega ao sino e ao popup de login de cada usuário de CADA empresa cadastrada.
												</p>
											</div>
										</div>
										<Switch
											id="isGlobal"
											checked={Boolean(values.isGlobal)}
											onCheckedChange={(marcado) =>
												setFieldValue("isGlobal", marcado)
											}
										/>
									</div>
								)}

								<DialogFooter>
									<Button
										type="button"
										variant="outline"
										onClick={fecharModal}
										disabled={isSubmitting}
									>
										Cancelar
									</Button>
									<Button type="submit" disabled={isSubmitting}>
										{isSubmitting && (
											<Loader2 className="h-4 w-4 animate-spin" />
										)}
										Salvar
									</Button>
								</DialogFooter>
							</Form>
						)}
					</Formik>
				</DialogContent>
			</Dialog>

			<MainHeader>
				<Title>
					{ehSuperAdmin ? "Comunicados SAAS & avisos internos" : "Avisos internos"}
				</Title>
				<MainHeaderButtonsWrapper>
					<Button
						onClick={() => {
							setSelecionado(null);
							setModalOpen(true);
						}}
					>
						<Plus className="h-4 w-4" />
						{ehSuperAdmin ? "Novo comunicado / aviso" : "Novo aviso"}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				{loading && (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Carregando…
					</p>
				)}

				{!loading && announcements.length === 0 && (
					<div className="rounded-xl border bg-card py-16 text-center">
						<p className="text-sm font-medium">Nenhum aviso publicado</p>
						<p className="mt-1 text-xs text-muted-foreground">
							Use avisos para comunicar a equipe sem sair do sistema.
						</p>
					</div>
				)}

				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
					{announcements.map((item) => {
						const prioridade = PRIORIDADES[item.priority] || PRIORIDADES[1];
						const eGlobal = Boolean(item.isGlobal);
						const podeEditar = ehSuperAdmin
							? true
							: !eGlobal && user?.profile === "admin";
						return (
							<div
								key={item.id}
								className={cn(
									"flex flex-col rounded-xl border bg-card p-4",
									eGlobal && "border-dashed border-primary/50 ring-1 ring-primary/10"
								)}
							>
								<div className="flex items-start justify-between gap-2">
									<div className="flex min-w-0 flex-1 flex-col gap-1">
										<div className="flex flex-wrap items-center gap-1.5">
											{/* Comunicado do dono do SaaS não leva selo: ele vai para
											    as empresas clientes assinado só pela equipe, sem
											    nome de pessoa. O selo ficou para o aviso interno,
											    onde saber que é da própria equipe faz diferença. */}
											{!eGlobal && (
												<Badge
													variant="default"
													className="h-5 gap-1 bg-sky-600/90 px-2 text-[10px] text-sky-50 hover:bg-sky-600"
												>
													<Users className="h-3 w-3" />
													INTERNO EQUIPE
												</Badge>
											)}
											<Badge variant={prioridade.variante} className="h-5 text-[10px]">
												{prioridade.rotulo}
											</Badge>
										</div>
										<h3 className="min-w-0 truncate font-medium">
											{item.title}
										</h3>
									</div>
								</div>

								<div className="mt-2 flex-1 rounded-md bg-muted/30 p-3">
									<AnnouncementsMarkdown>{item.text}</AnnouncementsMarkdown>
								</div>

								<div className="mt-3 flex items-center justify-between border-t pt-3">
									<span className="text-[11px] text-muted-foreground">
										{eGlobal ? "Equipe Perabit" : item.user?.name || "Sistema"} ·{" "}
										{format(parseISO(item.createdAt), "dd/MM/yyyy")}
										{!item.isActive && " · arquivado"}
									</span>
									{podeEditar && (
										<div className="flex gap-1">
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												aria-label="Editar aviso"
												onClick={() => {
													setSelecionado(item);
													setModalOpen(true);
												}}
											>
												<Pencil className="h-3.5 w-3.5" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												aria-label="Excluir aviso"
												onClick={() => {
													setSelecionado(item);
													setConfirmOpen(true);
												}}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</Button>
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</MainContainer>
	);
};

export default Announcements;
